import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Trash2 } from 'lucide-react';

// Constants from MaterialOrderForm
const COPPER_PIPE_SIZES = ['3/8"', '1/2"', '5/8"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2-1/8"', '2-5/8"', '3-1/8"', '3-5/8"', '4-1/8"', '5-1/8"', '6-1/8"'];
const IRON_PIPE_SIZES = ['3/8"', '1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '3-1/2"', '4"', '5"', '6"', '8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"'];
const INSULATION_THICKNESS = ['1/2"', '1"', '1-1/2"', '2"', '3"'];
const PVC_NUMBERS = Array.from({length: 40}, (_, i) => `#${i + 2}`);
const DUCT_THICKNESS = ['1"', '1.5"', '2"', '3"'];
const VICTAULIC_SIZES = ['3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '3-1/2"', '4"', '5"', '6"', '8"', '10"', '12"', '14"', '16"', '18"', '20"'];
const COUPLING_COVER_SIZES = ['5', '6', '7', '8', '9', '10', '12', '13', '14', '15', '16', '17', '18', '20', '22', '24'];

const JACKETING_TYPES = [
  'Venture Clad - Small Roll',
  'Venture Clad - Big Roll', 
  'Aluminum',
  'Flex-Clad',
  'Canvas',
  'Steel'
];

const ALUMINUM_PIPE_SIZES = [
  '1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', 
  '3-1/2"', '4"', '4-1/2"', '5"', '6"', '7"', '8"', '9"', '10"', 
  '12"', '14"', '15"', '16"', '17"', '18"', '19"', '20"'
];

const ALUMINUM_PIPE_THICKNESS = [
  '1"', '1-1/2"', '2"', '2-1/2"', '3"', '4"', '4-1/2"', '5"', '6"'
];

export default function StockManagementForm({ projects, onSubmit, onCancel }) {
  const [movementType, setMovementType] = useState('new_arrival');
  const [selectedProject, setSelectedProject] = useState('');
  const [notes, setNotes] = useState('');

  // Pipe Insulation State
  const [pipeItems, setPipeItems] = useState([
    { pipe_type: 'copper', size: '', thickness: '', quantity_ft: '', quantity_pieces: 0 }
  ]);

  // PVC Sheets State
  const [pvcSheets, setPvcSheets] = useState([
    { pvc_number: '', quantity: '' }
  ]);

  // PVC Roll State
  const [pvcRolls, setPvcRolls] = useState([
    { rolls: '' }
  ]);

  // PVC Fittings State
  const [pvcFittings, setPvcFittings] = useState({
    elbow_90: [{ pvc_number: '', quantity: '' }],
    elbow_45: [{ pvc_number: '', quantity: '' }],
    end_cap: [{ pvc_number: '', quantity: '' }],
    tee: [{ pvc_number: '', quantity: '' }],
    victaulic_90: [{ size: '', quantity: '' }],
    victaulic_45: [{ size: '', quantity: '' }],
    victaulic_tee: [{ size: '', quantity: '' }],
    coupling_covers: [{ size: '', quantity: '' }]
  });

  // Duct Insulation State
  const [ductItems, setDuctItems] = useState([
    { duct_type: 'flexible_wrap', thickness: '1"', quantity: 1 }
  ]);

  // Jacketing State
  const [jacketingItems, setJacketingItems] = useState([
    { type: '', rolls: '' }
  ]);

  // Aluminum Pipe Jacket State
  const [aluminumPipeJackets, setAluminumPipeJackets] = useState([
    { pipeSize: '', thickness: '', notes: '' }
  ]);

  // Tape State
  const [tapeItems, setTapeItems] = useState({
    silver_tape: { quantity: 0 },
    aerp_tape: { quantity: 0 },
    vinyl_white_tape: { quantity: 0 },
    cup_head_pins: [{ size: '1"', quantity: 0 }],
    corner_beads: { quantity: 0 },
    canvas_lagging: { quantity: 0 },
    rubber_glue: { quantity: 0 },
    polyken_tape: { quantity: 0 }
  });

  // Firestopping State
  const [firestoppingItems, setFirestoppingItems] = useState({
    self_level_silicon: { type: 'Tubes', quantity: 0, comments: '' },
    wall_silicon: { type: 'Tubes', quantity: 0, comments: '' },
    roxul_bag: { quantity: 0, comments: '' },
    collar: [{ size: '1.5"', quantity: 0, comments: '' }],
    wrap_strip: { quantity: 0, comments: '' },
    metacaulk_joint_strip: { quantity: 0, comments: '' },
    metal_strip: { quantity: 0, comments: '' },
    screw: { quantity: 0, comments: '' },
    z_frame: { quantity: 0, comments: '' }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const stockData = {
      movement_type: movementType,
      project_id: movementType === 'project_return' ? selectedProject : null,
      items: {
        pipe_insulation: pipeItems,
        pvc_sheets: pvcSheets,
        pvc_rolls: pvcRolls,
        pvc_fittings: pvcFittings,
        duct_insulation: ductItems,
        jacketing: jacketingItems,
        aluminum_pipe_jackets: aluminumPipeJackets,
        tape: tapeItems,
        firestopping: firestoppingItems
      },
      notes
    };

    onSubmit(stockData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Movement Type Selection */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <Label className="text-lg font-semibold">Stock Movement Type</Label>
        <RadioGroup value={movementType} onValueChange={setMovementType}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="new_arrival" id="new_arrival" />
            <Label htmlFor="new_arrival" className="cursor-pointer">
              New Arrivals - Fresh stock coming in
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="project_return" id="project_return" />
            <Label htmlFor="project_return" className="cursor-pointer">
              Returns from Projects - Materials returned from job sites
            </Label>
          </div>
        </RadioGroup>

        {/* Project Selection for Returns */}
        {movementType === 'project_return' && (
          <div className="mt-4">
            <Label>Select Project *</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject} required>
              <SelectTrigger>
                <SelectValue placeholder="Choose project..." />
              </SelectTrigger>
              <SelectContent>
                {projects && projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Tabbed Material Input */}
      <Tabs defaultValue="pipe" className="w-full">
        <div className="overflow-x-auto lg:overflow-x-visible" style={{ WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
          <TabsList className="flex gap-2 h-auto p-2 w-max lg:w-full lg:flex-wrap" style={{ minWidth: 'max-content' }}>
            <TabsTrigger value="pipe">Pipe</TabsTrigger>
            <TabsTrigger value="duct">Duct</TabsTrigger>
            <TabsTrigger value="pvc">PVC</TabsTrigger>
            <TabsTrigger value="fittings">Fittings</TabsTrigger>
            <TabsTrigger value="tape">Tape</TabsTrigger>
            <TabsTrigger value="firestopping">Firestopping</TabsTrigger>
            <TabsTrigger value="jacketing">Jacketing</TabsTrigger>
          </TabsList>
        </div>

        {/* Pipe Insulation Tab */}
        <TabsContent value="pipe" className="space-y-4">
          <h3 className="font-semibold">Pipe Insulation</h3>
          {pipeItems.map((item, index) => (
            <div key={index} className="grid grid-cols-6 gap-3 p-3 border rounded">
              <div>
                <Label>Pipe Type</Label>
                <Select
                  value={item.pipe_type}
                  onValueChange={(val) => {
                    const updated = [...pipeItems];
                    updated[index].pipe_type = val;
                    setPipeItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="copper">Copper</SelectItem>
                    <SelectItem value="iron">Iron</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Size</Label>
                <Select
                  value={item.size}
                  onValueChange={(val) => {
                    const updated = [...pipeItems];
                    updated[index].size = val;
                    setPipeItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(item.pipe_type === 'copper' ? COPPER_PIPE_SIZES : IRON_PIPE_SIZES).map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Thickness</Label>
                <Select
                  value={item.thickness}
                  onValueChange={(val) => {
                    const updated = [...pipeItems];
                    updated[index].thickness = val;
                    setPipeItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INSULATION_THICKNESS.map(thick => (
                      <SelectItem key={thick} value={thick}>{thick}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity (ft)</Label>
                <Input
                  type="number"
                  value={item.quantity_ft}
                  onChange={(e) => {
                    const updated = [...pipeItems];
                    updated[index].quantity_ft = e.target.value;
                    setPipeItems(updated);
                  }}
                />
              </div>
              <div>
                <Label>Pieces</Label>
                <Input
                  type="number"
                  value={item.quantity_pieces}
                  onChange={(e) => {
                    const updated = [...pipeItems];
                    updated[index].quantity_pieces = parseInt(e.target.value) || 0;
                    setPipeItems(updated);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPipeItems(pipeItems.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setPipeItems([...pipeItems, { pipe_type: 'copper', size: '', thickness: '', quantity_ft: '', quantity_pieces: 0 }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Pipe Item
          </Button>
        </TabsContent>

        {/* Duct Insulation Tab */}
        <TabsContent value="duct" className="space-y-4">
          <h3 className="font-semibold">Duct Insulation</h3>
          {ductItems.map((item, index) => (
            <div key={index} className="grid grid-cols-4 gap-3 p-3 border rounded">
              <div>
                <Label>Duct Type</Label>
                <Select
                  value={item.duct_type}
                  onValueChange={(val) => {
                    const updated = [...ductItems];
                    updated[index].duct_type = val;
                    setDuctItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flexible_wrap">Flexible Wrap</SelectItem>
                    <SelectItem value="duct_board">Duct Board</SelectItem>
                    <SelectItem value="rigid_board">Rigid Board</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Thickness</Label>
                <Select
                  value={item.thickness}
                  onValueChange={(val) => {
                    const updated = [...ductItems];
                    updated[index].thickness = val;
                    setDuctItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DUCT_THICKNESS.map(thick => (
                      <SelectItem key={thick} value={thick}>{thick}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const updated = [...ductItems];
                    updated[index].quantity = parseInt(e.target.value) || 0;
                    setDuctItems(updated);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDuctItems(ductItems.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setDuctItems([...ductItems, { duct_type: 'flexible_wrap', thickness: '1"', quantity: 1 }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Duct Item
          </Button>
        </TabsContent>

        {/* PVC Tab */}
        <TabsContent value="pvc" className="space-y-4">
          <h3 className="font-semibold">PVC Sheets</h3>
          {pvcSheets.map((item, index) => (
            <div key={index} className="grid grid-cols-3 gap-3 p-3 border rounded">
              <div>
                <Label>PVC Number</Label>
                <Select
                  value={item.pvc_number}
                  onValueChange={(val) => {
                    const updated = [...pvcSheets];
                    updated[index].pvc_number = val;
                    setPvcSheets(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PVC_NUMBERS.map(num => (
                      <SelectItem key={num} value={num}>{num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity (sheets)</Label>
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const updated = [...pvcSheets];
                    updated[index].quantity = e.target.value;
                    setPvcSheets(updated);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPvcSheets(pvcSheets.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setPvcSheets([...pvcSheets, { pvc_number: '', quantity: '' }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add PVC Sheet
          </Button>

          <h3 className="font-semibold mt-6">PVC Rolls</h3>
          {pvcRolls.map((item, index) => (
            <div key={index} className="grid grid-cols-2 gap-3 p-3 border rounded">
              <div>
                <Label>Number of Rolls</Label>
                <Input
                  type="number"
                  value={item.rolls}
                  onChange={(e) => {
                    const updated = [...pvcRolls];
                    updated[index].rolls = e.target.value;
                    setPvcRolls(updated);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPvcRolls(pvcRolls.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setPvcRolls([...pvcRolls, { rolls: '' }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add PVC Roll
          </Button>
        </TabsContent>

        {/* Fittings Tab */}
        <TabsContent value="fittings" className="space-y-6">
          <div>
            <h3 className="font-semibold mb-3">90° Elbows</h3>
            {pvcFittings.elbow_90.map((item, index) => (
              <div key={index} className="grid grid-cols-3 gap-3 p-3 border rounded mb-2">
                <div>
                  <Label>PVC Number</Label>
                  <Select
                    value={item.pvc_number}
                    onValueChange={(val) => {
                      const updated = {...pvcFittings};
                      updated.elbow_90[index].pvc_number = val;
                      setPvcFittings(updated);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PVC_NUMBERS.map(num => (
                        <SelectItem key={num} value={num}>{num}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const updated = {...pvcFittings};
                      updated.elbow_90[index].quantity = e.target.value;
                      setPvcFittings(updated);
                    }}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = {...pvcFittings};
                      updated.elbow_90 = updated.elbow_90.filter((_, i) => i !== index);
                      setPvcFittings(updated);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const updated = {...pvcFittings};
                updated.elbow_90.push({ pvc_number: '', quantity: '' });
                setPvcFittings(updated);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add 90° Elbow
            </Button>
          </div>

          <div>
            <h3 className="font-semibold mb-3">Victaulic 90° Elbows</h3>
            {pvcFittings.victaulic_90.map((item, index) => (
              <div key={index} className="grid grid-cols-3 gap-3 p-3 border rounded mb-2">
                <div>
                  <Label>Size</Label>
                  <Select
                    value={item.size}
                    onValueChange={(val) => {
                      const updated = {...pvcFittings};
                      updated.victaulic_90[index].size = val;
                      setPvcFittings(updated);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {VICTAULIC_SIZES.map(size => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const updated = {...pvcFittings};
                      updated.victaulic_90[index].quantity = e.target.value;
                      setPvcFittings(updated);
                    }}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated = {...pvcFittings};
                      updated.victaulic_90 = updated.victaulic_90.filter((_, i) => i !== index);
                      setPvcFittings(updated);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const updated = {...pvcFittings};
                updated.victaulic_90.push({ size: '', quantity: '' });
                setPvcFittings(updated);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Victaulic 90°
            </Button>
          </div>
        </TabsContent>

        {/* Tape Tab */}
        <TabsContent value="tape" className="space-y-4">
          <h3 className="font-semibold">Tape & Supplies</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Silver Tape (rolls)</Label>
              <Input
                type="number"
                value={tapeItems.silver_tape.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  silver_tape: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div>
              <Label>AERP Tape (rolls)</Label>
              <Input
                type="number"
                value={tapeItems.aerp_tape.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  aerp_tape: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div>
              <Label>Vinyl White Tape (rolls)</Label>
              <Input
                type="number"
                value={tapeItems.vinyl_white_tape.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  vinyl_white_tape: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div>
              <Label>Corner Beads</Label>
              <Input
                type="number"
                value={tapeItems.corner_beads.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  corner_beads: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div>
              <Label>Rubber Glue (gallons)</Label>
              <Input
                type="number"
                value={tapeItems.rubber_glue.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  rubber_glue: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div>
              <Label>PVC Tape (rolls)</Label>
              <Input
                type="number"
                value={tapeItems.polyken_tape.quantity}
                onChange={(e) => setTapeItems({
                  ...tapeItems,
                  polyken_tape: { quantity: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
          </div>
        </TabsContent>

        {/* Jacketing Tab */}

        {/* Firestopping Tab */}
        <TabsContent value="firestopping" className="space-y-4">
          <div className="space-y-4">
            <h4 className="font-semibold">Firestopping Materials</h4>
            
            {/* Simple items with just quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Self Level Silicon Sealant</Label>
                <div className="flex gap-2">
                  <Select
                    value={firestoppingItems.self_level_silicon.type}
                    onValueChange={(value) => setFirestoppingItems({
                      ...firestoppingItems,
                      self_level_silicon: { ...firestoppingItems.self_level_silicon, type: value }
                    })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tubes">Tubes</SelectItem>
                      <SelectItem value="Box">Box</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={firestoppingItems.self_level_silicon.quantity || ''}
                    onChange={(e) => setFirestoppingItems({
                      ...firestoppingItems,
                      self_level_silicon: { ...firestoppingItems.self_level_silicon, quantity: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Wall Silicon Sealant</Label>
                <div className="flex gap-2">
                  <Select
                    value={firestoppingItems.wall_silicon.type}
                    onValueChange={(value) => setFirestoppingItems({
                      ...firestoppingItems,
                      wall_silicon: { ...firestoppingItems.wall_silicon, type: value }
                    })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tubes">Tubes</SelectItem>
                      <SelectItem value="Box">Box</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={firestoppingItems.wall_silicon.quantity || ''}
                    onChange={(e) => setFirestoppingItems({
                      ...firestoppingItems,
                      wall_silicon: { ...firestoppingItems.wall_silicon, quantity: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Roxul Bag</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.roxul_bag.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    roxul_bag: { ...firestoppingItems.roxul_bag, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>

              <div>
                <Label>Wrap Strip (Boxes)</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.wrap_strip.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    wrap_strip: { ...firestoppingItems.wrap_strip, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>

              <div>
                <Label>Metacaulk Joint Strip (Boxes)</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.metacaulk_joint_strip.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    metacaulk_joint_strip: { ...firestoppingItems.metacaulk_joint_strip, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>

              <div>
                <Label>Metal Strip (Boxes)</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.metal_strip.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    metal_strip: { ...firestoppingItems.metal_strip, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>

              <div>
                <Label>Screw (Boxes)</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.screw.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    screw: { ...firestoppingItems.screw, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>

              <div>
                <Label>Z-Frame</Label>
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={firestoppingItems.z_frame.quantity || ''}
                  onChange={(e) => setFirestoppingItems({
                    ...firestoppingItems,
                    z_frame: { ...firestoppingItems.z_frame, quantity: parseInt(e.target.value) || 0 }
                  })}
                />
              </div>
            </div>

            {/* Collar with size selector */}
            <div>
              <Label>Collar</Label>
              {firestoppingItems.collar.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Select
                    value={item.size}
                    onValueChange={(value) => {
                      const updated = [...firestoppingItems.collar];
                      updated[index].size = value;
                      setFirestoppingItems({ ...firestoppingItems, collar: updated });
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='1.5"'>1.5"</SelectItem>
                      <SelectItem value='2"'>2"</SelectItem>
                      <SelectItem value='3"'>3"</SelectItem>
                      <SelectItem value='4"'>4"</SelectItem>
                      <SelectItem value='6"'>6"</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Quantity"
                    value={item.quantity || ''}
                    onChange={(e) => {
                      const updated = [...firestoppingItems.collar];
                      updated[index].quantity = parseInt(e.target.value) || 0;
                      setFirestoppingItems({ ...firestoppingItems, collar: updated });
                    }}
                  />
                  {firestoppingItems.collar.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = firestoppingItems.collar.filter((_, i) => i !== index);
                        setFirestoppingItems({ ...firestoppingItems, collar: updated });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFirestoppingItems({
                    ...firestoppingItems,
                    collar: [...firestoppingItems.collar, { size: '1.5"', quantity: 0, comments: '' }]
                  });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Collar Size
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="jacketing" className="space-y-4">
          <h3 className="font-semibold">Jacketing Materials</h3>
          {jacketingItems.map((item, index) => (
            <div key={index} className="grid grid-cols-3 gap-3 p-3 border rounded">
              <div>
                <Label>Type</Label>
                <Select
                  value={item.type}
                  onValueChange={(val) => {
                    const updated = [...jacketingItems];
                    updated[index].type = val;
                    setJacketingItems(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {JACKETING_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rolls</Label>
                <Input
                  type="number"
                  value={item.rolls}
                  onChange={(e) => {
                    const updated = [...jacketingItems];
                    updated[index].rolls = e.target.value;
                    setJacketingItems(updated);
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setJacketingItems(jacketingItems.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setJacketingItems([...jacketingItems, { type: '', rolls: '' }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Jacketing Item
          </Button>

          <h3 className="font-semibold mt-6">Aluminum Pipe Jacket</h3>
          {aluminumPipeJackets.map((item, index) => (
            <div key={index} className="grid grid-cols-4 gap-3 p-3 border rounded">
              <div>
                <Label>Pipe Size</Label>
                <Select
                  value={item.pipeSize}
                  onValueChange={(val) => {
                    const updated = [...aluminumPipeJackets];
                    updated[index].pipeSize = val;
                    setAluminumPipeJackets(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ALUMINUM_PIPE_SIZES.map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Thickness</Label>
                <Select
                  value={item.thickness}
                  onValueChange={(val) => {
                    const updated = [...aluminumPipeJackets];
                    updated[index].thickness = val;
                    setAluminumPipeJackets(updated);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ALUMINUM_PIPE_THICKNESS.map(thick => (
                      <SelectItem key={thick} value={thick}>{thick}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={item.notes}
                  onChange={(e) => {
                    const updated = [...aluminumPipeJackets];
                    updated[index].notes = e.target.value;
                    setAluminumPipeJackets(updated);
                  }}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAluminumPipeJackets(aluminumPipeJackets.filter((_, i) => i !== index))}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setAluminumPipeJackets([...aluminumPipeJackets, { pipeSize: '', thickness: '', notes: '' }])}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Aluminum Pipe Jacket
          </Button>
        </TabsContent>
      </Tabs>

      {/* Notes */}
      <div>
        <Label>Notes (Optional)</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional notes about this stock movement..."
          rows={3}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {movementType === 'new_arrival' ? 'Add to Stock' : 'Process Return'}
        </Button>
      </div>
    </form>
  );
}
